import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Header from '../components/Header'
import Tabs from '../components/Tabs'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
    colors,
} from '@mui/material'
import axios from 'axios'
import { useStore } from '../store/StoredData'
import { showCustomToast } from '../CustomToast'

const emptyEducationRow = () => ({ institution: '', degree: '', year: '' })

const emptyForm = {
    editor_name: '',
    slug: '',
    email_address: '',
    designation: '',
    organization: '',
    editorial_role: '',
    specialization: '',
    expertise: '',
    experience_years: '',
    short_bio: '',
    full_bio: '',
    editorial_oversight: '',
    current_position: '',
    previous_companies: '',
    previous_roles: '',
    education: [emptyEducationRow()],
    social_x: '',
    social_linkedin: '',
    social_facebook: '',
    profile_image_url: '',
    responsible_tab_ids: '',
    is_public: false,
}

const arrayToCsv = (value) => {
    if (!value) return ''
    if (Array.isArray(value)) return value.join(', ')
    return String(value)
}

const csvToArray = (value) => {
    if (!value || !String(value).trim()) return null
    return String(value)
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
}

const normalizeEducationForm = (value) => {
    if (!Array.isArray(value) || !value.length) return [emptyEducationRow()]
    return value.map((item) => ({
        institution: item?.institution != null ? String(item.institution) : '',
        degree: item?.degree != null ? String(item.degree) : '',
        year: item?.year != null ? String(item.year) : '',
    }))
}

const educationFormToPayload = (rows) => {
    if (!Array.isArray(rows)) return null
    const cleaned = rows
        .map((row) => ({
            institution: String(row?.institution || '').trim(),
            degree: String(row?.degree || '').trim(),
            year: String(row?.year || '').trim(),
        }))
        .filter((row) => row.institution || row.degree || row.year)
    return cleaned.length ? cleaned : null
}

const isPinnedValue = (value) =>
    value === true || value === 't' || value === 'true' || value === 1 || value === '1'

const formatArticleDate = (gmtLogtime) => {
    if (!gmtLogtime) return ''
    try {
        return new Date(gmtLogtime).toLocaleString()
    } catch (_) {
        return String(gmtLogtime)
    }
}

const EditorBio = () => {
    const history = useHistory()
    const { currentUser } = useStore()
    const [form, setForm] = useState(emptyForm)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [exists, setExists] = useState(false)
    const [error, setError] = useState('')
    const [accessChecked, setAccessChecked] = useState(false)
    const [isBioAdmin, setIsBioAdmin] = useState(false)
    const [editorOptions, setEditorOptions] = useState([])
    const [selectedEditorId, setSelectedEditorId] = useState('')
    const [linkedCmsUserId, setLinkedCmsUserId] = useState('')
    const [showLinkCreate, setShowLinkCreate] = useState(false)
    const [linkNcUserId, setLinkNcUserId] = useState('')
    const [linkName, setLinkName] = useState('')
    const [linkSlug, setLinkSlug] = useState('')
    const [linkEmail, setLinkEmail] = useState('')
    const [linking, setLinking] = useState(false)
    const [linkErrors, setLinkErrors] = useState({ ncUser: '', slug: '', email: '' })

    const [articles, setArticles] = useState([])
    const [maxPinned, setMaxPinned] = useState(9)
    const [articleTitle, setArticleTitle] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [loadingArticles, setLoadingArticles] = useState(false)
    const [savingPins, setSavingPins] = useState(false)
    const [pinError, setPinError] = useState('')

    const cmsUserId = currentUser?.userid

    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }))
    }

    const setEducationField = (index, key, value) => {
        setForm((prev) => {
            const education = [...(prev.education || [])]
            education[index] = { ...education[index], [key]: value }
            return { ...prev, education }
        })
    }

    const addEducationRow = () => {
        setForm((prev) => ({
            ...prev,
            education: [...(prev.education || []), emptyEducationRow()],
        }))
    }

    const removeEducationRow = (index) => {
        setForm((prev) => {
            const education = [...(prev.education || [])]
            education.splice(index, 1)
            return {
                ...prev,
                education: education.length ? education : [emptyEducationRow()],
            }
        })
    }

    const applyEditorToForm = (e) => {
        if (!e) {
            setExists(false)
            setForm(emptyForm)
            setArticles([])
            setLinkedCmsUserId('')
            return
        }
        setExists(true)
        setLinkedCmsUserId(e.cms_user_id != null ? String(e.cms_user_id) : '')
        setForm({
            editor_name: e.editor_name || '',
            slug: e.slug || '',
            email_address: e.email_address || '',
            designation: e.designation || '',
            organization: e.organization || '',
            editorial_role: e.editorial_role || '',
            specialization: e.specialization || '',
            expertise: arrayToCsv(e.expertise),
            experience_years:
                e.experience_years != null ? String(e.experience_years) : '',
            short_bio: e.short_bio || '',
            full_bio: e.full_bio || '',
            editorial_oversight: e.editorial_oversight || '',
            current_position: e.current_position || '',
            previous_companies: arrayToCsv(e.previous_companies),
            previous_roles: arrayToCsv(e.previous_roles),
            education: normalizeEducationForm(e.education),
            social_x: e.social_links?.x || e.social_links?.twitter || '',
            social_linkedin: e.social_links?.linkedin || '',
            social_facebook: e.social_links?.facebook || '',
            profile_image_url: e.profile_image_url || '',
            responsible_tab_ids: arrayToCsv(e.responsible_tab_ids),
            is_public: e.is_public === true,
        })
    }

    const loadArticles = async (filters = {}, editorId = selectedEditorId) => {
        if (!cmsUserId || !editorId) return
        setLoadingArticles(true)
        setPinError('')
        try {
            const resp = await axios.get('/api/editor-bio/articles', {
                params: {
                    cms_user_id: cmsUserId,
                    editor_id: editorId,
                    title: filters.title != null ? filters.title : articleTitle || undefined,
                    startdate: filters.startdate != null ? filters.startdate : startDate || undefined,
                    enddate: filters.enddate != null ? filters.enddate : endDate || undefined,
                    limit: 50,
                    page: 0,
                },
            })
            const payload = resp.data?.message
            if (!payload?.exists) {
                setArticles([])
                return
            }
            setMaxPinned(payload.max_pinned || 9)
            setArticles(
                (payload.articles || []).map((a) => ({
                    ...a,
                    is_pinned: isPinnedValue(a.is_pinned),
                    pinned_display_order: a.pinned_display_order != null ? Number(a.pinned_display_order) : '',
                }))
            )
        } catch (err) {
            console.error(err)
            setPinError(err?.response?.data?.message || 'Failed to load articles.')
            setArticles([])
        } finally {
            setLoadingArticles(false)
        }
    }

    const loadProfile = async (editorId = selectedEditorId) => {
        if (!cmsUserId) {
            setLoading(false)
            setError('Not logged in.')
            return
        }
        if (!editorId) {
            setLoading(false)
            applyEditorToForm(null)
            return
        }
        setLoading(true)
        setError('')
        try {
            const resp = await axios.get('/api/editor-bio', {
                params: { cms_user_id: cmsUserId, editor_id: editorId },
            })
            const payload = resp.data?.message
            if (payload?.exists && payload?.editor) {
                applyEditorToForm(payload.editor)
                if (payload.editor.editor_id) {
                    setSelectedEditorId(String(payload.editor.editor_id))
                }
            } else {
                applyEditorToForm(null)
            }
        } catch (err) {
            console.error(err)
            setError(err?.response?.data?.message || 'Failed to load editor profile.')
            applyEditorToForm(null)
        } finally {
            setLoading(false)
        }
    }

    const loadEditorOptions = async () => {
        const resp = await axios.get('/api/editor-bio/editors', {
            params: { cms_user_id: cmsUserId },
        })
        const rows = Array.isArray(resp.data?.message) ? resp.data.message : []
        setEditorOptions(rows)
        return rows
    }

    useEffect(() => {
        let cancelled = false
        const boot = async () => {
            if (!cmsUserId) {
                setAccessChecked(true)
                setIsBioAdmin(false)
                setLoading(false)
                setError('Not logged in.')
                return
            }
            setLoading(true)
            setError('')
            try {
                await axios.get('/api/editor-bio/access', {
                    params: { cms_user_id: cmsUserId },
                })
                if (cancelled) return
                setIsBioAdmin(true)
                const rows = await loadEditorOptions()
                if (cancelled) return
                if (rows.length) {
                    const firstId = String(rows[0].editor_id)
                    setSelectedEditorId(firstId)
                    await loadProfile(firstId)
                } else {
                    setSelectedEditorId('')
                    setLoading(false)
                }
            } catch (err) {
                if (cancelled) return
                console.error(err)
                setIsBioAdmin(false)
                setError(
                    err?.response?.data?.message ||
                        'You do not have access to Editor Bio.'
                )
                setLoading(false)
            } finally {
                if (!cancelled) setAccessChecked(true)
            }
        }
        boot()
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cmsUserId])

    useEffect(() => {
        if (exists && cmsUserId && selectedEditorId) {
            loadArticles({}, selectedEditorId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exists, cmsUserId, selectedEditorId])

    const handleEditorSelect = async (editorId) => {
        setShowLinkCreate(false)
        setSelectedEditorId(editorId)
        if (!editorId) {
            applyEditorToForm(null)
            return
        }
        await loadProfile(editorId)
    }

    const isValidEmailFormat = (email) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim())

    const isValidSlugFormat = (slug) =>
        /^[a-z0-9]+([a-z0-9-]*[a-z0-9])?$/.test(String(slug).trim())

    const handleOpenLinkCreate = () => {
        setSelectedEditorId('')
        applyEditorToForm(null)
        setShowLinkCreate(true)
        setLinkNcUserId('')
        setLinkName('')
        setLinkSlug('')
        setLinkEmail('')
        setError('')
        setLinkErrors({ ncUser: '', slug: '', email: '' })
    }

    const handleLinkCreate = async () => {
        if (!cmsUserId) {
            setError('Not logged in.')
            return
        }

        const errors = { ncUser: '', slug: '', email: '' }

        const trimmedSlug = linkSlug.trim()
        const trimmedEmail = linkEmail.trim()

        if (!linkNcUserId.trim()) {
            errors.ncUser = 'NC User ID is required.'
        }

        if (!trimmedSlug) {
            errors.slug = 'Slug is required.'
        } else if (!isValidSlugFormat(trimmedSlug)) {
            errors.slug = 'Lowercase letters, numbers and hyphens only — e.g. firstname-lastname'
        } else if (editorOptions.some((o) => String(o.slug || '').trim() === trimmedSlug)) {
            errors.slug = 'This slug is already used by another editor.'
        }

        if (!trimmedEmail) {
            errors.email = 'Email address is required.'
        } else if (!isValidEmailFormat(trimmedEmail)) {
            errors.email = 'Enter a valid email address.'
        } else if (editorOptions.some((o) => String(o.email_address || '').trim().toLowerCase() === trimmedEmail.toLowerCase())) {
            errors.email = 'This email address is already used by another editor.'
        }

        if (!linkName.trim()) {
            setError('editor_name is required.')
            return
        }

        if (errors.ncUser || errors.slug || errors.email) {
            setLinkErrors(errors)
            return
        }

        setLinkErrors({ ncUser: '', slug: '', email: '' })
        setLinking(true)
        setError('')
        try {
            const resp = await axios.post('/api/editor-bio/link', {
                cms_user_id: cmsUserId,
                link_cms_user_id: linkNcUserId.trim(),
                editor_name: linkName.trim(),
                slug: linkSlug.trim(),
                email_address: linkEmail.trim(),
            })
            const editor = resp.data?.message?.editor
            showCustomToast('Editor linked. Bio is hidden until you enable Show bio on website.', 'success')
            setShowLinkCreate(false)
            await loadEditorOptions()
            if (editor?.editor_id) {
                const id = String(editor.editor_id)
                setSelectedEditorId(id)
                await loadProfile(id)
            }
        } catch (err) {
            console.error(err)
            setError(err?.response?.data?.message || 'Failed to link editor.')
        } finally {
            setLinking(false)
        }
    }

    const handlePhotoChange = async (event) => {
        const file = event.target.files?.[0]
        if (!file) return
        setUploading(true)
        setError('')
        try {
            const data = new FormData()
            data.append('profile_photo', file)
            if (form.slug?.trim()) {
                data.append('slug', form.slug.trim())
            }
            const resp = await axios.post('/api/editor-bio/photo', data)
            const url = resp.data?.message?.profile_image_url
            if (url) {
                setField('profile_image_url', url)
            } else {
                setError('Photo upload succeeded but no URL returned.')
            }
        } catch (err) {
            console.error(err)
            setError(err?.response?.data?.message || 'Photo upload failed.')
        } finally {
            setUploading(false)
            event.target.value = ''
        }
    }

    const handleSave = async () => {
        if (!cmsUserId) {
            setError('Not logged in.')
            return
        }
        if (!selectedEditorId) {
            setError('Select an editor, or link a new one with an NC User ID first.')
            return
        }
        if (!form.editor_name.trim() || !form.slug.trim() || !form.email_address.trim()) {
            setError('editor_name, slug, and email_address are required.')
            return
        }
        if (!form.short_bio.trim() || !form.full_bio.trim()) {
            setError('short_bio and full_bio are required.')
            return
        }

        setSaving(true)
        setError('')
        try {
            const body = {
                cms_user_id: cmsUserId,
                editor_id: selectedEditorId,
                editor_name: form.editor_name.trim(),
                slug: form.slug.trim(),
                email_address: form.email_address.trim(),
                designation: form.designation.trim() || null,
                organization: form.organization.trim() || null,
                editorial_role: form.editorial_role.trim() || null,
                specialization: form.specialization.trim() || null,
                expertise: csvToArray(form.expertise),
                experience_years: form.experience_years.trim()
                    ? Number(form.experience_years)
                    : null,
                short_bio: form.short_bio.trim() || null,
                full_bio: form.full_bio.trim() || null,
                editorial_oversight: form.editorial_oversight.trim() || null,
                current_position: form.current_position.trim() || null,
                previous_companies: csvToArray(form.previous_companies),
                previous_roles: csvToArray(form.previous_roles),
                education: educationFormToPayload(form.education),
                social_links: {
                    x: form.social_x.trim() || undefined,
                    linkedin: form.social_linkedin.trim() || undefined,
                    facebook: form.social_facebook.trim() || undefined,
                },
                profile_image_url: form.profile_image_url.trim() || null,
                responsible_tab_ids: csvToArray(form.responsible_tab_ids),
                is_public: form.is_public === true,
            }

            const resp = await axios.put('/api/editor-bio', body)
            const editor = resp.data?.message?.editor
            if (editor) {
                setExists(true)
                if (editor.editor_id) {
                    setSelectedEditorId(String(editor.editor_id))
                }
                if (editor.cms_user_id != null) {
                    setLinkedCmsUserId(String(editor.cms_user_id))
                }
                setForm((prev) => ({
                    ...prev,
                    editor_name: editor.editor_name || prev.editor_name,
                    slug: editor.slug || prev.slug,
                    email_address: editor.email_address || prev.email_address,
                    profile_image_url: editor.profile_image_url || prev.profile_image_url,
                    education: normalizeEducationForm(editor.education),
                    is_public: editor.is_public === true,
                }))
                try {
                    await loadEditorOptions()
                } catch (_) {
                    /* keep current options */
                }
                showCustomToast('Editor profile updated.', 'success')
            } else {
                showCustomToast('Saved.', 'success')
            }
        } catch (err) {
            console.error(err)
            setError(err?.response?.data?.message || 'Failed to save editor profile.')
        } finally {
            setSaving(false)
        }
    }

    const pinnedCount = articles.filter((a) => a.is_pinned).length

    const renumberPinned = (list) => {
        const pinned = list
            .filter((a) => a.is_pinned)
            .sort((a, b) => {
                const ao = Number(a.pinned_display_order) || 999
                const bo = Number(b.pinned_display_order) || 999
                return ao - bo
            })
        const orderMap = new Map()
        pinned.forEach((a, idx) => orderMap.set(String(a.id), idx + 1))
        return list.map((a) => {
            if (!a.is_pinned) return { ...a, pinned_display_order: '' }
            return { ...a, pinned_display_order: orderMap.get(String(a.id)) }
        })
    }

    const togglePin = (articleId, checked) => {
        setPinError('')
        setArticles((prev) => {
            const currentPinned = prev.filter((a) => a.is_pinned).length
            if (checked && currentPinned >= maxPinned) {
                setPinError(`You can pin at most ${maxPinned} articles.`)
                return prev
            }
            const next = prev.map((a) => {
                if (String(a.id) !== String(articleId)) return a
                if (checked) {
                    const nextOrder = currentPinned + 1
                    return { ...a, is_pinned: true, pinned_display_order: nextOrder }
                }
                return { ...a, is_pinned: false, pinned_display_order: '' }
            })
            return renumberPinned(next)
        })
    }

    const setPinOrder = (articleId, value) => {
        setArticles((prev) =>
            prev.map((a) =>
                String(a.id) === String(articleId)
                    ? { ...a, pinned_display_order: value }
                    : a
            )
        )
    }

    const handleSearchArticles = () => {
        loadArticles({
            title: articleTitle,
            startdate: startDate,
            enddate: endDate,
        })
    }

    const handleSavePins = async () => {
        if (!cmsUserId || !exists) {
            setPinError('Create and save your editor profile first.')
            return
        }

        const pinned = articles.filter((a) => a.is_pinned)
        if (pinned.length > maxPinned) {
            setPinError(`You can pin at most ${maxPinned} articles.`)
            return
        }

        const pins = []
        const orders = new Set()
        for (const a of pinned) {
            const orderNum = Number(a.pinned_display_order)
            if (!Number.isFinite(orderNum) || orderNum < 1) {
                setPinError('Each pinned article needs an order number (1, 2, 3…).')
                return
            }
            if (orders.has(orderNum)) {
                setPinError('Pinned order numbers must be unique.')
                return
            }
            orders.add(orderNum)
            pins.push({
                article_id: String(a.id),
                pinned_display_order: Math.trunc(orderNum),
            })
        }

        setSavingPins(true)
        setPinError('')
        try {
            if (!selectedEditorId) {
                setPinError('Select an editor before saving pins.')
                setSavingPins(false)
                return
            }
            await axios.put('/api/editor-bio/articles/pins', {
                cms_user_id: cmsUserId,
                editor_id: selectedEditorId,
                pins,
            })
            showCustomToast('Pinned articles saved.', 'success')
            await loadArticles({}, selectedEditorId)
        } catch (err) {
            console.error(err)
            setPinError(err?.response?.data?.message || 'Failed to save pinned articles.')
        } finally {
            setSavingPins(false)
        }
    }

    const fieldSx = { mb: 1.5 }

    if (accessChecked && !isBioAdmin) {
        return (
            <>
                <Header />
                <Tabs />
                <Box mt={4} mx={4}>
                    <Typography variant='h6' color={colors.indigo[800]} mb={1}>
                        Editor Bio
                    </Typography>
                    <Typography color='error' mb={2}>
                        {error || 'You do not have access to Editor Bio.'}
                    </Typography>
                    <Button
                        variant='outlined'
                        onClick={() => history.push('/home')}
                        sx={{ borderColor: colors.indigo[800], color: colors.indigo[800] }}
                    >
                        Back to Home
                    </Button>
                </Box>
            </>
        )
    }

    return (
        <>
            <Header />
            <Tabs />
            <Box mt={2} mx={4} mb={4}>
                <Typography variant='h6' color={colors.indigo[800]} mb={1}>
                    Editor Bio
                </Typography>
                <Typography variant='body2' color='text.secondary' mb={2}>
                    Admin only. Use <strong>Link new editor by User ID</strong> to add someone (starts as
                    draft). Then select them, edit the bio, and turn on Show bio on website when ready.
                </Typography>

                <Box display='flex' flexWrap='wrap' gap={2} alignItems='center' mb={2}>
                    <FormControl size='small' sx={{ minWidth: 280 }}>
                        <InputLabel id='editor-bio-select-label'>Editor</InputLabel>
                        <Select
                            labelId='editor-bio-select-label'
                            label='Editor'
                            value={selectedEditorId}
                            onChange={(e) => handleEditorSelect(e.target.value)}
                            disabled={loading || linking}
                        >
                            {editorOptions.map((ed) => (
                                <MenuItem key={String(ed.editor_id)} value={String(ed.editor_id)}>
                                    {ed.editor_name || ed.slug}
                                    {ed.slug ? ` (${ed.slug})` : ''}
                                    {ed.is_public === true ? '' : ' — draft'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant='outlined'
                        size='small'
                        onClick={handleOpenLinkCreate}
                        disabled={linking}
                        sx={{ borderColor: colors.indigo[800], color: colors.indigo[800] }}
                    >
                        Link new editor by User ID
                    </Button>
                </Box>

                {showLinkCreate ? (
                    <Box
                        mb={2}
                        p={2}
                        border={1}
                        borderColor={colors.indigo[100]}
                        borderRadius={1}
                        bgcolor={colors.grey[50]}
                    >
                        <Typography variant='subtitle2' color={colors.indigo[800]} mb={1}>
                            Link NC CMS Account → create editor profile (starts as draft)
                        </Typography>
                        <Box display='flex' flexWrap='wrap' gap={1.5}>
                            <TextField
                                required
                                label='NC User ID'
                                size='small'
                                value={linkNcUserId}
                                onChange={(e) => setLinkNcUserId(e.target.value)}
                                sx={{ minWidth: 160 }}
                                error={!!linkErrors.ncUser}
                                helperText={linkErrors.ncUser || "The editor's NC User ID (visible in their header after login)"}
                            />
                            <TextField
                                required
                                label='editor_name'
                                size='small'
                                value={linkName}
                                onChange={(e) => setLinkName(e.target.value)}
                                sx={{ minWidth: 180 }}
                            />
                            <TextField
                                required
                                label='slug'
                                size='small'
                                value={linkSlug}
                                onChange={(e) => setLinkSlug(e.target.value)}
                                sx={{ minWidth: 160 }}
                                error={!!linkErrors.slug}
                                helperText={linkErrors.slug || 'Lowercase, numbers and hyphens only — e.g. firstname-lastname'}
                            />
                            <TextField
                                required
                                label='email_address'
                                size='small'
                                value={linkEmail}
                                onChange={(e) => setLinkEmail(e.target.value)}
                                sx={{ minWidth: 220 }}
                                error={!!linkErrors.email}
                                helperText={linkErrors.email || 'Public contact email shown on bio — readers use this to reach the writer'}
                            />
                            <Button
                                variant='contained'
                                size='small'
                                onClick={handleLinkCreate}
                                disabled={linking}
                                sx={{ bgcolor: colors.indigo[800], '&:hover': { bgcolor: colors.indigo[900] } }}
                            >
                                {linking ? 'Linking…' : 'Link & create'}
                            </Button>
                            <Button
                                variant='text'
                                size='small'
                                onClick={() => setShowLinkCreate(false)}
                                disabled={linking}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </Box>
                ) : null}

                {loading ? (
                    <Box display='flex' justifyContent='center' py={6}>
                        <CircularProgress size={28} />
                    </Box>
                ) : (
                    <>
                        {error ? (
                            <Typography color='error' mb={2} fontSize={14}>
                                {error}
                            </Typography>
                        ) : null}

                        {!exists && !showLinkCreate ? (
                            <Typography color='text.secondary' mb={2} fontSize={14}>
                                Select an editor above, or link a new one with an NC CMS email.
                            </Typography>
                        ) : null}

                        {exists ? (
                            <>
                        <Box
                            display='flex'
                            flexWrap='wrap'
                            gap={2}
                            mb={2}
                            alignItems='center'
                            p={1.5}
                            border={1}
                            borderColor={colors.indigo[100]}
                            borderRadius={1}
                        >
                            <Typography variant='body2'>
                                {linkedCmsUserId
                                    ? `✓ CMS User ID: ${linkedCmsUserId}`
                                    : '⚠ No CMS Account Linked'}
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.is_public === true}
                                        onChange={(e) => setField('is_public', e.target.checked)}
                                        sx={{
                                            color: colors.indigo[800],
                                            '&.Mui-checked': { color: colors.indigo[800] },
                                        }}
                                    />
                                }
                                label='Show bio on website'
                            />
                        </Box>

                        <Box
                            display='flex'
                            flexWrap='wrap'
                            gap={2}
                            mb={2}
                            p={2}
                            border={1}
                            borderColor={colors.indigo[100]}
                            borderRadius={1}
                            bgcolor={colors.indigo[50]}
                        >
                            <Box>
                                {form.profile_image_url ? (
                                    <Box
                                        component='img'
                                        src={form.profile_image_url}
                                        alt='Profile'
                                        sx={{
                                            width: 120,
                                            height: 120,
                                            objectFit: 'cover',
                                            border: `1px solid ${colors.indigo[200]}`,
                                        }}
                                    />
                                ) : (
                                    <Box
                                        width={120}
                                        height={120}
                                        display='flex'
                                        alignItems='center'
                                        justifyContent='center'
                                        border={`1px dashed ${colors.indigo[300]}`}
                                        fontSize={12}
                                        color={colors.grey[600]}
                                    >
                                        No photo
                                    </Box>
                                )}
                            </Box>
                            <Box display='flex' flexDirection='column' justifyContent='center' gap={1}>
                                <Button
                                    variant='outlined'
                                    component='label'
                                    size='small'
                                    disabled={uploading}
                                    sx={{ borderColor: colors.indigo[800], color: colors.indigo[800] }}
                                >
                                    {uploading ? 'Uploading…' : 'Upload Profile Photo'}
                                    <input
                                        hidden
                                        type='file'
                                        accept='image/png,image/jpeg,image/jpg,image/webp'
                                        onChange={handlePhotoChange}
                                    />
                                </Button>
                                <TextField
                                    label='profile_image_url'
                                    size='small'
                                    fullWidth
                                    value={form.profile_image_url}
                                    onChange={(e) => setField('profile_image_url', e.target.value)}
                                    sx={{ minWidth: 320 }}
                                />
                            </Box>
                        </Box>

                        <Box display='flex' flexWrap='wrap' gap={2}>
                            <Box width={{ xs: '100%', md: '48%' }}>
                                <TextField
                                    required
                                    label='editor_name'
                                    size='small'
                                    fullWidth
                                    value={form.editor_name}
                                    onChange={(e) => setField('editor_name', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    required
                                    label='slug'
                                    size='small'
                                    fullWidth
                                    helperText='Lowercase, numbers and hyphens only — e.g. firstname-lastname · URL: /editors/<slug>'
                                    value={form.slug}
                                    onChange={(e) => setField('slug', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    required
                                    label='email_address'
                                    size='small'
                                    fullWidth
                                    value={form.email_address}
                                    onChange={(e) => setField('email_address', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label='designation'
                                    size='small'
                                    fullWidth
                                    value={form.designation}
                                    onChange={(e) => setField('designation', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label='organization'
                                    size='small'
                                    fullWidth
                                    value={form.organization}
                                    onChange={(e) => setField('organization', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label='editorial_role'
                                    size='small'
                                    fullWidth
                                    value={form.editorial_role}
                                    onChange={(e) => setField('editorial_role', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label='specialization'
                                    size='small'
                                    fullWidth
                                    value={form.specialization}
                                    onChange={(e) => setField('specialization', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label='experience_years'
                                    size='small'
                                    fullWidth
                                    type='number'
                                    value={form.experience_years}
                                    onChange={(e) => setField('experience_years', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label='expertise (comma-separated)'
                                    size='small'
                                    fullWidth
                                    value={form.expertise}
                                    onChange={(e) => setField('expertise', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label='previous_companies (comma-separated)'
                                    size='small'
                                    fullWidth
                                    value={form.previous_companies}
                                    onChange={(e) => setField('previous_companies', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label='previous_roles (comma-separated)'
                                    size='small'
                                    fullWidth
                                    value={form.previous_roles}
                                    onChange={(e) => setField('previous_roles', e.target.value)}
                                    sx={fieldSx}
                                />
                                <Box sx={{ mb: 1.5 }}>
                                    <Typography variant='body2' sx={{ mb: 1, fontWeight: 500 }}>
                                        education
                                    </Typography>
                                    {(form.education || []).map((row, index) => (
                                        <Box
                                            key={`edu-${index}`}
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 1,
                                                mb: 1,
                                                alignItems: 'flex-start',
                                            }}
                                        >
                                            <TextField
                                                label='institution'
                                                size='small'
                                                value={row.institution}
                                                onChange={(e) =>
                                                    setEducationField(index, 'institution', e.target.value)
                                                }
                                                sx={{ flex: '1 1 140px', minWidth: 120 }}
                                            />
                                            <TextField
                                                label='degree'
                                                size='small'
                                                value={row.degree}
                                                onChange={(e) =>
                                                    setEducationField(index, 'degree', e.target.value)
                                                }
                                                sx={{ flex: '1 1 140px', minWidth: 120 }}
                                            />
                                            <TextField
                                                label='year'
                                                size='small'
                                                value={row.year}
                                                onChange={(e) =>
                                                    setEducationField(index, 'year', e.target.value)
                                                }
                                                sx={{ width: 90 }}
                                            />
                                            <Button
                                                size='small'
                                                color='inherit'
                                                onClick={() => removeEducationRow(index)}
                                                disabled={(form.education || []).length <= 1}
                                                sx={{ mt: 0.5 }}
                                            >
                                                Remove
                                            </Button>
                                        </Box>
                                    ))}
                                    <Button size='small' variant='outlined' onClick={addEducationRow}>
                                        Add education
                                    </Button>
                                </Box>
                                <TextField
                                    label='responsible_tab_ids (comma-separated)'
                                    size='small'
                                    fullWidth
                                    value={form.responsible_tab_ids}
                                    onChange={(e) => setField('responsible_tab_ids', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label='social_links.x'
                                    size='small'
                                    fullWidth
                                    value={form.social_x}
                                    onChange={(e) => setField('social_x', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label='social_links.linkedin'
                                    size='small'
                                    fullWidth
                                    value={form.social_linkedin}
                                    onChange={(e) => setField('social_linkedin', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label='social_links.facebook'
                                    size='small'
                                    fullWidth
                                    value={form.social_facebook}
                                    onChange={(e) => setField('social_facebook', e.target.value)}
                                    sx={fieldSx}
                                />
                            </Box>

                            <Box width={{ xs: '100%', md: '48%' }}>
                                <TextField
                                    label='current_position'
                                    size='small'
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={form.current_position}
                                    onChange={(e) => setField('current_position', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    label='editorial_oversight'
                                    size='small'
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={form.editorial_oversight}
                                    onChange={(e) => setField('editorial_oversight', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    required
                                    label='short_bio'
                                    size='small'
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    value={form.short_bio}
                                    onChange={(e) => setField('short_bio', e.target.value)}
                                    sx={fieldSx}
                                />
                                <TextField
                                    required
                                    label='full_bio'
                                    size='small'
                                    fullWidth
                                    multiline
                                    minRows={10}
                                    value={form.full_bio}
                                    onChange={(e) => setField('full_bio', e.target.value)}
                                    sx={fieldSx}
                                />
                            </Box>
                        </Box>

                        <Box mt={2}>
                            <Button
                                variant='contained'
                                onClick={handleSave}
                                disabled={saving || uploading}
                                sx={{ bgcolor: colors.indigo[800], '&:hover': { bgcolor: colors.indigo[900] } }}
                            >
                                {saving ? 'Saving…' : 'Update Profile'}
                            </Button>
                        </Box>

                        <Box
                            mt={4}
                            p={2}
                            border={1}
                            borderColor={colors.indigo[100]}
                            borderRadius={1}
                        >
                            <Typography variant='h6' color={colors.indigo[800]} mb={0.5}>
                                Pinned articles
                            </Typography>
                            <Typography variant='body2' color='text.secondary' mb={2}>
                                Choose up to {maxPinned} articles to pin on this editor&apos;s public bio page.
                                Use title and date filters to find them. ({pinnedCount}/{maxPinned} pinned)
                            </Typography>

                                    <Box display='flex' flexWrap='wrap' gap={1.5} alignItems='center' mb={2}>
                                        <TextField
                                            label='Title'
                                            size='small'
                                            value={articleTitle}
                                            onChange={(e) => setArticleTitle(e.target.value)}
                                            sx={{ minWidth: 220 }}
                                        />
                                        <TextField
                                            label='From'
                                            size='small'
                                            type='date'
                                            InputLabelProps={{ shrink: true }}
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                        <TextField
                                            label='To'
                                            size='small'
                                            type='date'
                                            InputLabelProps={{ shrink: true }}
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                        <Button
                                            variant='outlined'
                                            size='small'
                                            onClick={handleSearchArticles}
                                            disabled={loadingArticles}
                                            sx={{ borderColor: colors.indigo[800], color: colors.indigo[800] }}
                                        >
                                            Search
                                        </Button>
                                    </Box>

                                    {pinError ? (
                                        <Typography color='error' mb={1.5} fontSize={14}>
                                            {pinError}
                                        </Typography>
                                    ) : null}

                                    {loadingArticles ? (
                                        <Box display='flex' justifyContent='center' py={3}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : articles.length === 0 ? (
                                        <Typography fontSize={14} color='text.secondary'>
                                            No articles found for this editor (check author_id / filters).
                                        </Typography>
                                    ) : (
                                        <Box display='flex' flexDirection='column' gap={1}>
                                            {articles.map((article) => (
                                                <Box
                                                    key={article.id}
                                                    display='flex'
                                                    flexWrap='wrap'
                                                    alignItems='center'
                                                    gap={1}
                                                    p={1}
                                                    border={1}
                                                    borderColor={colors.grey[200]}
                                                    borderRadius={1}
                                                >
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={!!article.is_pinned}
                                                                onChange={(e) =>
                                                                    togglePin(article.id, e.target.checked)
                                                                }
                                                                sx={{
                                                                    color: colors.indigo[800],
                                                                    '&.Mui-checked': { color: colors.indigo[800] },
                                                                }}
                                                            />
                                                        }
                                                        label='Pin'
                                                    />
                                                    <TextField
                                                        label='Order'
                                                        size='small'
                                                        type='number'
                                                        disabled={!article.is_pinned}
                                                        value={article.is_pinned ? article.pinned_display_order : ''}
                                                        onChange={(e) => setPinOrder(article.id, e.target.value)}
                                                        sx={{ width: 90 }}
                                                        inputProps={{ min: 1, max: maxPinned }}
                                                    />
                                                    <Box flex={1} minWidth={200}>
                                                        <Typography fontSize={14} fontWeight={500}>
                                                            {article.title}
                                                        </Typography>
                                                        <Typography fontSize={12} color='text.secondary'>
                                                            {article.id} · {formatArticleDate(article.gmt_logtime)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    <Box mt={2}>
                                        <Button
                                            variant='contained'
                                            onClick={handleSavePins}
                                            disabled={savingPins || loadingArticles}
                                            sx={{ bgcolor: colors.indigo[800], '&:hover': { bgcolor: colors.indigo[900] } }}
                                        >
                                            {savingPins ? 'Saving pins…' : 'Save pins'}
                                        </Button>
                                    </Box>
                        </Box>
                            </>
                        ) : null}
                    </>
                )}
            </Box>
        </>
    )
}

export default EditorBio
